import React, { FC } from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from "recharts";
import { AssessmentOutlined } from "@mui/icons-material";
import { formatNumberMetric } from "../../utils/commonUtils";
import { COLORS } from "../../utils/constants";

interface IRegionDataItem {
	region: string;
	approved: number;
	pending: number;
	rejected: number;
	total: number;
}

interface IInterceptorDataItem {
	interceptor: string;
	approved: number;
	pending: number;
	rejected: number;
	total: number;
}

interface IRegionalBarChartProps {
	data: IRegionDataItem[];
	interceptorData?: IInterceptorDataItem[];
	isInterceptorView?: boolean;
	showLabels?: boolean;
	chartRef?: React.RefObject<any>;
}

const RegionalBarChart: FC<IRegionalBarChartProps> = ({ data, interceptorData, isInterceptorView = false, showLabels = false, chartRef }) => {
	// Use interceptor data if provided and has items, otherwise use region data
	// If interceptor view is requested but no interceptor data is available, fall back to region data
	const hasInterceptorData = interceptorData && Array.isArray(interceptorData) && interceptorData.length > 0;
	const shouldUseInterceptorView = isInterceptorView && hasInterceptorData;
	
	const chartData: (IRegionDataItem | IInterceptorDataItem)[] = shouldUseInterceptorView ? interceptorData! : data;
	const dataKey = shouldUseInterceptorView ? "interceptor" : "region";
	const title = shouldUseInterceptorView ? "Interceptor Performance Analysis" : "Regional Performance Analysis";
	const subtitle = shouldUseInterceptorView ? "Status breakdown by interceptor" : "Status breakdown by region";

	// Check if we have data before processing
	const hasData = Array.isArray(chartData) && chartData.length > 0;

	// Sort by total challans (descending) and take top 10
	const top10Data = hasData ? [...chartData].sort((a, b) => b.total - a.total).slice(0, 10) : [];

	return (
		<Card
			sx={{
				bgcolor: "background.paper",
				border: "1px solid rgba(0, 0, 0, 0.08)",
				borderRadius: 3,
				boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
				mb: 3
			}}
		>
			<CardContent sx={{ p: 3, pb: 0, "&:last-child": { pb: 0 } }}>
				<Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
					<Box>
						<Typography variant="h6" fontWeight={600} sx={{ color: "text.primary", mb: 0.5 }}>
							{title}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
							{subtitle}
						</Typography>
					</Box>
					{/* Legend in top right */}
					<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<Box sx={{ width: 12, height: 12, bgcolor: COLORS.approved, borderRadius: 0.5 }} />
							<Typography variant="body2" sx={{ color: COLORS.approved, fontSize: "0.875rem" }}>
								Issued
							</Typography>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<Box sx={{ width: 12, height: 12, bgcolor: COLORS.pending, borderRadius: 0.5 }} />
							<Typography variant="body2" sx={{ color: COLORS.pending, fontSize: "0.875rem" }}>
								Pending
							</Typography>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<Box sx={{ width: 12, height: 12, bgcolor: COLORS.rejected, borderRadius: 0.5 }} />
							<Typography variant="body2" sx={{ color: COLORS.rejected, fontSize: "0.875rem" }}>
								Rejected
							</Typography>
						</Box>
					</Box>
				</Box>
				{hasData ? (
					<Box sx={{ mb: -2 }} ref={chartRef}>
						<ResponsiveContainer width="100%" height={400}>
							<BarChart data={top10Data} margin={{ bottom: 0 }}>
								<CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
								<XAxis
									dataKey={dataKey}
									angle={-45}
									textAnchor="end"
									height={120}
									tick={{ fontSize: 11 }}
									interval={0}
								/>
								<YAxis tickFormatter={(value: number) => formatNumberMetric(value)} />
								<Tooltip formatter={(value: any) => formatNumberMetric(value)} />
								<Bar 
									dataKey="approved" 
									fill={COLORS.approved} 
									stackId="status" 
									radius={[0, 0, 0, 0]} 
									name="Issued"
								>
									{showLabels && (
										<LabelList 
											dataKey="approved" 
											position="center" 
											fill="#fff" 
											fontSize={11}
											fontWeight="bold"
											formatter={(value: any) => (value && value > 0 ? formatNumberMetric(value) : '')}
										/>
									)}
								</Bar>
								<Bar 
									dataKey="pending" 
									fill={COLORS.pending} 
									stackId="status" 
									radius={[0, 0, 0, 0]} 
									name="Pending"
								>
									{showLabels && (
										<LabelList 
											dataKey="pending" 
											position="center" 
											fill="#fff" 
											fontSize={11}
											fontWeight="bold"
											formatter={(value: any) => (value && value > 0 ? formatNumberMetric(value) : '')}
										/>
									)}
								</Bar>
								<Bar 
									dataKey="rejected" 
									fill={COLORS.rejected} 
									stackId="status" 
									radius={[2, 2, 0, 0]} 
									name="Rejected"
								>
									{showLabels && (
										<LabelList 
											dataKey="rejected" 
											position="center" 
											fill="#fff" 
											fontSize={11}
											fontWeight="bold"
											formatter={(value: any) => (value && value > 0 ? formatNumberMetric(value) : '')}
										/>
									)}
								</Bar>
							</BarChart>
						</ResponsiveContainer>
					</Box>
				) : (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							height: 400,
							py: 4
						}}
					>
						<AssessmentOutlined sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
						<Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
							No Data Available
						</Typography>
						<Typography variant="body2" color="text.disabled">
							{shouldUseInterceptorView
								? "No interceptor data found for the selected filters"
								: "No regional data found for the selected filters"}
						</Typography>
					</Box>
				)}
			</CardContent>
		</Card>
	);
};

export default RegionalBarChart;
